import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Button, IconButton } from '@/components/Button';
import { ViewSidebar, type SidebarView } from '@/components/ViewSidebar';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { Applied, Chips, DeskFrame, FilterButton, ListBar, LoadMore, NoAccess, Pager, PhoneFrame, SearchBox, SplitFrame, useQueryState } from '../shipments/ListParts';
import { useExceptionCounts, useExceptionList, useExceptionTypeCounts } from './api';
import { ExceptionCard } from './ExceptionCard';
import { ExceptionsTable } from './ExceptionsTable';
import { ExceptionDetail } from './ExceptionDetail';
import { ExceptionActionDialog, type ExceptionAction } from './ExceptionActionDialog';
import { ExceptionCreateFlow } from './ExceptionCreateFlow';
import { FILTER_KEYS, FilterSheet, type ExFilters } from './FilterSheet';
import { FILTERS, VIEWS } from './status';
import type { ExceptionItem } from './types';
import s from './ExceptionsPage.module.css';

const PLACEHOLDER = '订单号 / 客户 / 产品 / 批次 / 物流单号 / 备注';
const PAGE_SIZE = 20;

/**
 * 异常售后（/exceptions）。视图 = 状态（老接口 status 参数，默认「未闭环」同老版）；类型、责任方、库存影响进「筛选」。
 * - 手机：根页大标题 + 状态 chips + 三格要动手的数 + 异常卡；点卡进 /exceptions/:id?order=；
 * - iPad：列表栏 + 详情分栏（?id=&order= 写进 URL）；
 * - 桌面：表格 + 分页，点行进详情页。
 * ?order=<orderId> 可只看一张订单的异常（订单详情跳过来时用）。
 */
export function ExceptionsPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const q = useQueryState();
  const [panel, setPanel] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [action, setAction] = useState<ExceptionAction | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [searching, setSearching] = useState(!!q.get('q'));
  const viewKey = q.get('view') || 'pending';
  const view = VIEWS.find((v) => v.key === viewKey) || VIEWS[0];
  const keyword = q.get('q') || '';
  const orderId = q.get('order');
  const selectedId = q.get('id');
  const page = Math.max(1, Number(q.get('page')) || 1);
  const filters: ExFilters = Object.fromEntries(FILTER_KEYS.map((k) => [k, q.get(k)]).filter(([, v]) => v));
  const fkey = JSON.stringify(filters);
  const base = useMemo(() => ({ ...filters, keyword: keyword || undefined, orderId }), [fkey, keyword, orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = useMemo(() => ({ ...base, status: view.status }), [base, view.status]);
  const phone = ff === 'phone';
  const split = ff === 'tablet';
  const list = useExceptionList(query, PAGE_SIZE, ff !== 'desktop', page);
  const counts = useExceptionCounts(base, refreshVersion);
  const typeCounts = useExceptionTypeCounts(phone && can('exceptions.read'), refreshVersion);
  const showAmount = can('orders.amount.read');
  const canClose = can('exceptions.close');
  const canResolve = can('exceptions.resolve');
  const canCreate = can('exceptions.create') && (can('orders.read.all') || can('orders.read.own') || can('orders.read.assigned'));
  const changed = () => { list.reload(); setRefreshVersion((version) => version + 1); };
  const actionEl = action && <ExceptionActionDialog action={action} can={can} onClose={() => setAction(null)} onSuccess={changed} />;
  const createEl = createOpen && <ExceptionCreateFlow initialOrderId={orderId} onClose={() => setCreateOpen(false)} onSuccess={changed} />;

  if (!can('exceptions.read')) return <NoAccess what="异常售后" legacy="/order-exceptions" />;

  const search = (v: string) => q.set({ q: v || undefined, page: undefined, id: undefined, io: undefined });
  const chips = VIEWS.map((v) => ({ key: v.key, label: v.label, count: counts ? v.count(counts) : undefined }));
  const mobileChips = [...chips.slice(0, 5), ...FILTERS.find((group) => group.key === 'type')!.opts.map((option) => ({ key: `type:${option.value}`, label: option.label, count: !keyword && !orderId ? typeCounts[option.value] : undefined })), ...chips.slice(5)];
  const mobileActive = filters.type ? `type:${filters.type}` : view.key;
  const pickView = (k: string) => q.set({ view: k === 'pending' ? undefined : k, type: undefined, page: undefined, id: undefined, io: undefined });
  const pickMobileChip = (key: string) => key.startsWith('type:')
    ? q.set({ view: 'all', type: key.slice(5), page: undefined, id: undefined, io: undefined })
    : pickView(key);
  const sidebarViews: SidebarView[] = [
    ...(['pending', 'processing', 'resolved', 'closed'] as const).map((key, index) => {
      const v = VIEWS.find((item) => item.key === key)!;
      return { key, label: v.label, icon: ['alert', 'set', 'ok', 'flag'][index], count: counts ? v.count(counts) : undefined, hot: key === 'pending' || key === 'processing' };
    }),
    ...([['DAMAGED', '破损', 'box'], ['SHORT_SHIPPED', '少发', 'truck'], ['RESHIPMENT', '补发', 'inbox'], ['RETURNED', '退货', 'down']] as const).map(([type, label, icon], index) => ({ key: `type:${type}`, label, icon, separator: index === 0 })),
    { key: 'responsibility', label: '责任与损失', icon: 'chart', separator: true, to: '/exceptions/responsibility' },
    { key: 'all', label: '全部异常', icon: 'book', count: counts?.all },
    { key: 'open', label: '未闭环', icon: 'alert', count: counts?.open },
  ];
  const sidebarActive = filters.type ? `type:${filters.type}` : view.key;
  const pickSidebar = (key: string) => key.startsWith('type:')
    ? q.set({ view: 'all', type: key.slice(5), page: undefined, id: undefined, io: undefined })
    : pickView(key);

  const orderNo = orderId ? list.items[0]?.order.id === orderId ? list.items[0].order.orderNo : '此订单' : '';
  const applied = [
    ...(orderId ? [{ key: 'order', label: `只看 ${orderNo}` }] : []),
    ...FILTER_KEYS.filter((k) => filters[k]).map((k) => ({ key: k, label: FILTERS.find((g) => g.key === k)?.opts.find((o) => o.value === filters[k])?.label || String(filters[k]) })),
  ];
  const appliedEl = (
    <Applied
      items={applied}
      onRemove={(k) => q.set({ [k]: undefined, page: undefined, id: undefined, io: undefined })}
      onClear={() => q.set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])), order: undefined, page: undefined, id: undefined, io: undefined })}
    />
  );
  const filterCount = FILTER_KEYS.filter((k) => filters[k]).length;
  const panelEl = panel && (
    <FilterSheet
      value={filters}
      sheet={phone}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        q.set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, f[k]])), page: undefined, id: undefined, io: undefined });
      }}
    />
  );

  const filtered = keyword || applied.length > 0;
  const body = (() => {
    if (list.error && !list.items.length) return <ErrorState title="异常列表加载失败" message={list.error.message} detail="GET /api/order-exceptions" onRetry={list.reload} />;
    if (list.loading && !list.items.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      return filtered ? (
        <Empty icon="srch" title="没有符合条件的异常" action={<Button onClick={() => q.set({ q: undefined, order: undefined, ...Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])) })}>清空搜索和筛选</Button>}>
          换个关键词，或者去掉一些筛选条件再看看。
        </Empty>
      ) : (
        <Empty icon="ok" title={`「${view.label}」里没有异常`}>
          {view.key === 'open' || view.key === 'pending' ? '售后都处理完了。新登记的少发、破损、退货会出现在这里。' : '别的状态视图里可能有记录。'}
        </Empty>
      );
    }
    return null;
  })();

  const listBar = (
    <ListBar>
      <span>{list.total} 条{keyword && ` · 搜索「${keyword}」`}</span>
      <span>按状态、更新时间排序</span>
    </ListBar>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <>
        <PhoneFrame
          title="异常售后"
          actions={<><IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />{canCreate && <IconButton name="plus" label="登记异常" onClick={() => setCreateOpen(true)} />}</>}
          top={
            <>
              {searching && <div className={s.psearch}><SearchBox variant="phone" value={keyword} placeholder={PLACEHOLDER} onSearch={search} /></div>}
              <div className={s.chipbar}><Chips items={mobileChips} active={mobileActive} onPick={pickMobileChip} /><FilterButton count={filterCount} onClick={() => setPanel(true)} /></div>
              {appliedEl}
            </>
          }
        >
          {counts && (
            <div className={s.stats}>
              <button className={`${s.stat} ${s.bad}`} onClick={() => pickView('pending')}><span>待处理</span><b className="tnum">{counts.pending}</b></button>
              <button className={`${s.stat} ${s.hl}`} onClick={() => pickView('processing')}><span>处理中</span><b className="tnum">{counts.processing}</b></button>
              <Link className={`${s.stat} ${s.statLink}`} to="/exceptions/responsibility"><span>责任与损失 ›</span><b className="tnum">{counts.all}</b><small>条异常</small></Link>
            </div>
          )}
          {listBar}
          {body || list.items.map((e) => <ExceptionCard key={e.id} e={e} showAmount={showAmount} canClose={canClose} canResolve={canResolve} onClose={(item) => setAction({ kind: 'close', item })} onResolve={(item) => setAction({ kind: 'resolve', item })} />)}
          {!body && <LoadMore hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />}
        </PhoneFrame>
        {panelEl}
        {actionEl}
        {createEl}
      </>
    );
  }

  /* ------------------------------ iPad：列表 + 详情 ------------------------------ */
  if (split) {
    // 选中项写进 URL：id = 异常，io = 它所属的订单（详情按订单取；不能复用 order，那是列表的过滤条件）
    const inSplit = (e: ExceptionItem) => {
      const n = new URLSearchParams(q.sp);
      n.set('id', e.id);
      n.set('io', e.order.id);
      return `/exceptions?${n.toString()}`;
    };
    return (
      <>
        <SplitFrame
          title={view.label}
          count={list.total}
          tools={<><Link className={s.responsibilityLink} to="/exceptions/responsibility" aria-label="责任与损失">统计</Link><FilterButton variant="box" count={filterCount} onClick={() => setPanel(true)} />{canCreate && <IconButton name="plus" label="登记异常" onClick={() => setCreateOpen(true)} />}</>}
          top={
            <>
              <div className={s.lsearch}><SearchBox variant="side" value={keyword} placeholder={PLACEHOLDER} onSearch={search} /></div>
              <Chips variant="side" items={chips} active={view.key} onPick={pickView} />
              {appliedEl}
            </>
          }
          list={
            <>
              {listBar}
              {body || list.items.map((e) => <ExceptionCard key={e.id} e={e} to={inSplit(e)} selected={e.id === selectedId} showAmount={showAmount} canClose={canClose} canResolve={canResolve} onClose={(item) => setAction({ kind: 'close', item })} onResolve={(item) => setAction({ kind: 'resolve', item })} />)}
              {!body && <LoadMore hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />}
            </>
          }
          detail={
            selectedId ? (
              <ExceptionDetail
                key={selectedId}
                id={selectedId}
                orderId={q.get('io') || orderId}
                seed={list.items.find((x) => x.id === selectedId)}
                embedded
                linkFor={inSplit}
                onChanged={changed}
              />
            ) : (
              <Empty icon="cards" title="选择左侧异常查看详情">详情在这里打开，不用跳页。</Empty>
            )
          }
        />
        {panelEl}
        {actionEl}
        {createEl}
      </>
    );
  }

  /* ------------------------------ 桌面：表格 ------------------------------ */
  return (
    <>
      <DeskFrame
        title={filters.type ? sidebarViews.find((v) => v.key === sidebarActive)?.label || view.label : view.label}
        sub={`共 ${list.total} 条`}
        sidebar={<ViewSidebar title="异常视图" views={sidebarViews} active={sidebarActive} onPick={pickSidebar} />}
        tools={
          <>
            <SearchBox variant="desk" value={keyword} placeholder={PLACEHOLDER} onSearch={search} />
            <FilterButton variant="box" count={filterCount} onClick={() => setPanel(true)} />
            <Button icon="ref" onClick={list.reload}>刷新</Button>
            {canCreate && <Button icon="plus" variant="primary" onClick={() => setCreateOpen(true)}>登记异常</Button>}
          </>
        }
        top={appliedEl}
        footer={!body && <Pager page={list.page} pageSize={PAGE_SIZE} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={(p) => q.set({ page: p > 1 ? String(p) : undefined })} />}
      >
        {counts && (
          <div className={s.strip}>
            <span><i>待处理</i><b className="tnum neg">{counts.pending}</b></span>
            <span><i>处理中</i><b className="tnum">{counts.processing}</b></span>
            <span><i>今日新增</i><b className="tnum">{counts.today}</b></span>
            <span><i>需要补发</i><b className="tnum">{counts.reshipCandidates}</b></span>
            <span><i>等待退货入库</i><b className="tnum">{counts.returnCandidates}</b></span>
            <span><i>已处理</i><b className="tnum">{counts.resolved}</b></span>
          </div>
        )}
        {body || <ExceptionsTable items={list.items} showAmount={showAmount} canClose={canClose} canResolve={canResolve} onClose={(item) => setAction({ kind: 'close', item })} onResolve={(item) => setAction({ kind: 'resolve', item })} />}
      </DeskFrame>
      {panelEl}
      {actionEl}
      {createEl}
    </>
  );
}
